import { Injectable } from '@angular/core';

import { ApplicationSettings } from 'dspx-core';

import { ApiBaseRoute } from '../constants/api-endpoints.constants';
import { APP_DATA_KEYS } from '../constants/app-data.constants';
import { SYNC_MESSENGER_MESSAGE_TYPES } from '../constants/messaging.constants';
import { NETWORK_CONNECTIVITY } from '../constants/network-connectivity.constants';
import { LogLevel } from '../constants/log.constants';

import { NetworkConnectivity } from '../helpers/network-connectivity';
import { SyncMessenger } from '../helpers/sync-messenger';

import { SyncMessengerService } from './sync-messenger.service';
import { AppDataService } from './app-data.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkConnectivityService {

  private reconnectionPollingTimeoutId: number = null;
  private connectivityChangeListeners: Map<string, () => void> = new Map();

  private online: boolean;
  private networkConnectivity: NetworkConnectivity;
  private syncMessenger: SyncMessenger;

  constructor(
    appSettings: ApplicationSettings,
    syncMessengerService: SyncMessengerService,
    appDataService: AppDataService) {

    this.online = true;
    
    const logLevel = appDataService.getAppData<LogLevel>(APP_DATA_KEYS.logLevel);
    const baseApiUrl = appSettings.apiUrl + ApiBaseRoute;

    this.networkConnectivity = new NetworkConnectivity(logLevel, baseApiUrl);
    this.syncMessenger = syncMessengerService.getSyncMessenger();
  }

  isOnline(): boolean {
    return this.online;
  }

  async checkConnectivity(): Promise<void> {
    const online = await this.networkConnectivity.checkConnectivity();
    if (this.online !== online) {
      this.syncMessenger.postMessage(online ? SYNC_MESSENGER_MESSAGE_TYPES.networkOnline : SYNC_MESSENGER_MESSAGE_TYPES.networkOffline);
      this.online = online;
      this.notifyConnectivityChangeListeners();
    }
    if (!online) {
      this.startReconnectionPolling();
    }
    else {
      this.stopReconnectionPolling();
    }
  }

  addConnectivityChangeListener(name: string, listener: () => void): void {
    this.connectivityChangeListeners.set(name, listener);
  }

  removeConnectivityChangeListener(name: string): void {
    this.connectivityChangeListeners.delete(name);
  }

  private notifyConnectivityChangeListeners(): void {
    for (const listener of this.connectivityChangeListeners.values()) {
      listener();
    }
  }

  private startReconnectionPolling(): void {
    this.stopReconnectionPolling();
    this.reconnectionPollingTimeoutId = <number><unknown>setTimeout(() => this.checkConnectivity(), NETWORK_CONNECTIVITY.reconnectionPollingIntervalMs);
  }

  private stopReconnectionPolling(): void {
    if (this.reconnectionPollingTimeoutId) {
      clearTimeout(this.reconnectionPollingTimeoutId);
      this.reconnectionPollingTimeoutId = null;
    }
  }
}
